/**
 * Is this "browser" a crawler, a synthetic monitor, or our own prerenderer?
 *
 * Used to skip telemetry that only makes sense for real humans. A bot cannot
 * file a bug report and its session replay is unwatchable, so recording either
 * spends bundle, main-thread time and Sentry quota to produce noise.
 *
 * Deliberately errs toward treating a client as REAL. A false positive costs
 * one visitor's telemetry; a false negative costs a little noise. Neither is
 * severe, so this stays a cheap heuristic rather than a fingerprinting exercise.
 *
 * Checked at call time, not module scope: the prerenderer sets __PRERENDER__ via
 * evaluateOnNewDocument, and reading it lazily keeps this testable.
 */

/**
 * Generic crawler vocabulary, anchored on the LEFT to a non-letter so it does
 * not fire on ordinary words that happen to contain one of these.
 */
const GENERIC_UA =
  /(^|[^a-z])(crawler|crawling|spider|slurp|headless|lighthouse|pagespeed|gtmetrix|pingdom|uptime|archiver|scraper)/i;

/**
 * "bot" needs its own rule, and the direction is the opposite of GENERIC_UA.
 *
 * Real crawlers concatenate it onto a brand - "Googlebot/2.1", "bingbot/2.0",
 * "AhrefsBot/7.0", "GPTBot/1.0" - so anchoring on the left misses all of them.
 * But a bare /bot/ matches the phone brand "CUBOT" and "Abbott", which would
 * silently strip error reporting from those real users.
 *
 * Only "bot/" and a trailing "bot" count. A wider delimiter class was tried and
 * is WRONG: `_`, `;` and `)` all appear straight after an Android model name,
 * so "CUBOT_NOTE_20", "CUBOT; wv" and "CUBOT)" were all classified synthetic.
 * Those are the standard ro.product.model shapes; only the space-separated
 * "CUBOT NOTE 20" survived, which is the single variant the first test pinned.
 *
 * Crawlers that use some other delimiter ("SomeBot;") fall through to REAL.
 * That is the intended bias: a false negative costs a little noise, a false
 * positive costs a real user's error reporting.
 */
const BOT_TOKEN_UA = /bot\/|bot$/i;

/**
 * Agents carrying no generic token at all, including the synthetic monitors
 * and Google's non-Googlebot fetchers, which otherwise read as real browsers.
 */
const NAMED_UA =
  /(facebookexternalhit|bytespider|bingpreview|yandex|yeti|duckduckgo|baidu|petalbot|embedly|whatsapp|telegram|googleother|inspectiontool|mediapartners-google|statuscake|ptst|gtmetrix)/i;

export function isSyntheticClient(): boolean {
  if (typeof window === 'undefined') return false;

  // Our own prerenderer (scripts/prerender.mjs sets this before any app code).
  if ((window as { __PRERENDER__?: boolean }).__PRERENDER__ === true) return true;

  if (typeof navigator === 'undefined') return false;

  // Puppeteer/Playwright/Selenium set this. Lighthouse and our prerenderer do too.
  if (navigator.webdriver === true) return true;

  const ua = navigator.userAgent || '';
  return GENERIC_UA.test(ua) || BOT_TOKEN_UA.test(ua) || NAMED_UA.test(ua);
}
